import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../utils/api';
import Alert from '../components/Alert';
import QuotationPreviewModal from '../components/QuotationPreviewModal';
import { 
    Building2, 
    ArrowLeft, 
    Plus, 
    Info, 
    Lightbulb, 
    Loader2 
} from 'lucide-react';
import './CreateProfessionalQuotation.css';

const CreateProfessionalQuotation = () => {
    const navigate = useNavigate();
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [success, setSuccess] = useState('');
    const [showPreviewModal, setShowPreviewModal] = useState(false);
    const [createdQuotationId, setCreatedQuotationId] = useState(null);


    const [formData, setFormData] = useState({
        quotationDate: new Date().toISOString().split('T')[0],

        // Section 2.1 - Only Project Type and Area are dynamic
        projectDetails: {
            projectType: 'Office',
            projectName: 'Office Interior',
            projectLocation: 'Pragati IT Park, Yogi Chowk',
            projectAddress: 'Surat, Gujarat.',
            projectArea: 594
        },

        // Section 1.2 - Client Details (pre-filled, editable)
        clientDetails: {
            name: 'Vishal Vasoya',
            physicalAddress: 'Same as above mentioned',
            cellPhone: '+91-70484 84880',
            email: ''
        },

        // Section 1.3 - Designer Details (from settings)
        designerDetails: {
            firmName: '',
            headDesignerName: 'Architect: Priyankkumar Suvagiya',
            credentials: '',
            physicalAddress: '',
            cellPhone: '',
            email: '',
            licenseNumber: 'CA/2023/155632'
        },

        // Section 2.2 - Work Stages (completely static)
        workStages: [
            { stageNumber: 1, stageName: 'Inception/ Study', description: '' },
            { stageNumber: 2, stageName: 'Concept & Viability (concept design)', description: '' },
            { stageNumber: 3, stageName: 'Client Approval on Design', description: '' },
            { stageNumber: 4, stageName: 'Design Development', description: '' },
            { stageNumber: 5, stageName: 'Design Handover', description: '' },
            { stageNumber: 6, stageName: 'Construction', description: '' },
            { stageNumber: 7, stageName: 'Close – out', description: '' }
        ],

        // Section 5 - Charges (only amounts are dynamic)
        charges: {
            stages: [
                { stageName: 'Work Stage 1', amount: 200000, percentage: '(advance amount)' },
                { stageName: 'Work Stage 2', amount: 200000, percentage: '(25% work done)' },
                { stageName: 'Work Stage 3', amount: 200000, percentage: '(50% work done)' },
                { stageName: 'Work Stage 4', amount: 80000, percentage: '(final work done)' }
            ],
            additionalNotes: 'If required 3D for any of the project those charges would be applied per room 5000/-\nThe amount is not fixed and may increase or decrease over time'
        },

        // Section 2.3 - Inception (only totalFees and perSqftRate are dynamic)
        inceptionOfWork: {
            totalFees: 40000,
            perSqftRate: 70,
            notes: "We'll be providing every detail drawing needed and we'll be there for every selection process.\nAlso refer the [4. DESIGN SERVICES] clause mentioned below for more detail of our services\nVisits as required visiting charges would apply per visit 1500/- (only if site is out of Surat)\nVisiting charges would apply per visit 500/- (only if visits get extend from 15 visits within Surat"
        },

        // Section 3 - General Accord (completely static, pre-filled)
        generalAccord: [
            'The Client as mentioned above has expressed an interest in the services of our firm to Develop and design the above location.',
            'The Client will accept sole financial responsibility for structure charges of this property',
            'The Firm holds the required experience and license necessary to fulfil the client\'s needs as Well as all mentioned terms set forth',
            'We will be working through our official E-Mail Id or calls. All the drawings and the queries Will be answered via E-Mail or calls.',
            'For queries and any suggestions, we kindly request you to contact the designer directly.',
            'We also request you to set the acceptable deadline for the work to avoid confusion.',
            'In case of any dispute in future leading to dissolving of work, you will not be allowed to use Our drawings and designs to execute site.',
            'Also, in case of dissolvent, you will not be exempt from paying the firm of their work which is been done till the date of dissolvent.'
        ],

        // Section 4 - Design Services (completely static, pre-filled)
        designServices: [
            'Our Design firm is in full understanding and will comply with any state regulations with regard to the design and structure of the property mentioned.',
            'Our design Firm will complete any and all requirements within the time frame of this agreement within state\'s standards and regulations.',
            'Not to start any Work Stage without the client confirmation.',
            'Make only such alterations, additions and omissions in the agreed drawings and Specifications as are required in the client\'s interest, and inform the client of such alterations, Additions, omissions and of their cost implications.',
            'Keep the client informed of any significant increases in cost during construction.',
            'As for any particular Project after we finish designing part, we will be handing over our Conceptual drawings to discuss within a week name.',
            'All the changes drawings within a week after our discussion and if there are more changes will Take accordingly.',
            'Detailing drawings we will hand them over as we start finishing them after the design is Freeze, those drawings will be ready to execute.'
        ],

        // Section 6 - Close Out (only months is dynamic)
        closeOutPeriod: 'Our firm will upon such Observation conduct checks for any defects, recalls, and deficiencies of all deliverables. Our Firm will notify the Client of such findings. No design and observation conduct will happen After the project completion period and if needed charges would apply accordingly.',
        closeOutMonths: 2.5
    });

    useEffect(() => {
        fetchSettings();
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const fetchSettings = async () => {
        try {
            const response = await api.get('/settings');
            const settingsData = response.data.settings;
            

            // Pre-populate designer details from settings
            setFormData(prev => ({
                ...prev,
                designerDetails: {
                    ...prev.designerDetails,
                    firmName: settingsData.companyName || 'VIRASTRA DESIGN STUDIO',
                    physicalAddress: settingsData.address || '703, Surat Digital Valley, Mota Varachha, Surat, 394105',
                    cellPhone: settingsData.phone || '+91-88662 40824',
                    email: settingsData.email || 'virastra.designstudio@gmail.com'
                }
            }));
        } catch (err) {
            setError('Failed to load settings');
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        try {
            setLoading(true);
            setError('');

            // Filter out empty array items
            const cleanedFormData = {
                ...formData,
                generalAccord: formData.generalAccord.filter(item => item.trim() !== ''),
                designServices: formData.designServices.filter(item => item.trim() !== '')
            };

            const response = await api.post('/professional-quotations', cleanedFormData);

            if (response.data.success) {
                setSuccess('Project Assurance created successfully! Opening preview...');
                setCreatedQuotationId(response.data.quotation._id);
                setTimeout(() => {
                    setLoading(false);
                    setShowPreviewModal(true);
                }, 800);
            }
        } catch (err) {
            setError(err.response?.data?.message || 'Failed to create Project Assurance');
            setLoading(false);
        }
    };

    const handleModalClose = (saved) => {
        setShowPreviewModal(false);
        if (createdQuotationId) {
            navigate(`/professional-quotations/${createdQuotationId}`);
        }
    };

    return (
        <div className="create-professional-quotation-container">
            {showPreviewModal && createdQuotationId && (
                <QuotationPreviewModal
                    quotationId={createdQuotationId}
                    onClose={handleModalClose}
                />
            )}

            <div className="page-header" style={{ marginBottom: '30px' }}>
                <div>
                    <h1 style={{ fontSize: '28px', fontWeight: '700', marginBottom: '5px', display: 'flex', alignItems: 'center', gap: '10px' }}>
                        <Building2 size={28} className="anim-pulse" /> Create Project Assurance
                    </h1>
                    <p style={{ color: '#666', fontSize: '15px', margin: 0 }}>
                        Quick Form - Only Key Values Needed
                    </p>
                </div>
                <button
                    type="button"
                    className="btn btn-secondary"
                    onClick={() => navigate('/project-assurance')}
                    style={{ display: 'flex', alignItems: 'center', gap: '8px' }}
                >
                    <ArrowLeft size={18} /> Back to project-assurance
                </button>
            </div>

            {error && <Alert type="error" message={error} onClose={() => setError('')} />}
            {success && <Alert type="success" message={success} onClose={() => setSuccess('')} />}

            <form onSubmit={handleSubmit}>
                {/* SECTION 1 - CONTRACTING DETAILS */}
                <div className="card" style={{ marginBottom: '25px', borderColor: '#f97316', borderWidth: '2px' }}>
                    <div className="card-header" style={{ background: 'linear-gradient(135deg, #f97316 0%, #ea580c 100%)', color: 'white', fontSize: '18px', fontWeight: '600' }}>
                        1. CONTRACTING DETAILS
                    </div>
                    <div className="card-body">
                        {/* 1.1 - Project Details */}
                        <div style={{ marginBottom: '30px' }}>
                            <h4 style={{ fontSize: '16px', fontWeight: '600', marginBottom: '15px', color: '#374151' }}>
                                1.1 - Project
                            </h4>
                            <div className="form-row">
                                <div className="form-group">
                                    <label className="form-label">Project Type <span className="required">*</span></label>
                                    <select
                                        value={formData.projectDetails.projectType}
                                        onChange={(e) => setFormData(prev => ({
                                            ...prev,
                                            projectDetails: { ...prev.projectDetails, projectType: e.target.value }
                                        }))}
                                        className="form-control"
                                        required
                                    >
                                        <option value="Residential">Residential</option>
                                        <option value="Hospital">Hospital</option>
                                        <option value="Commercial">Commercial</option>
                                        <option value="Office">Office</option>
                                    </select>
                                </div>

                                <div className="form-group">
                                    <label className="form-label">Project Name <span className="required">*</span></label>
                                    <input
                                        type="text"
                                        value={formData.projectDetails.projectName}
                                        onChange={(e) => setFormData(prev => ({
                                            ...prev,
                                            projectDetails: { ...prev.projectDetails, projectName: e.target.value }
                                        }))}
                                        className="form-control"
                                        placeholder="Office Interior"
                                        required
                                    />
                                </div>
                            </div>

                            <div className="form-group">
                                <label className="form-label">Project Location <span className="required">*</span></label>
                                <input
                                    type="text"
                                    value={formData.projectDetails.projectLocation}
                                    onChange={(e) => setFormData(prev => ({
                                        ...prev,
                                        projectDetails: { ...prev.projectDetails, projectLocation: e.target.value }
                                    }))}
                                    className="form-control"
                                    placeholder="Pragati IT Park, Yogi Chowk"
                                    required
                                />
                            </div>

                            <div className="form-row">
                                <div className="form-group">
                                    <label className="form-label">Physical Address <span className="required">*</span></label>
                                    <textarea
                                        value={formData.projectDetails.projectAddress}
                                        onChange={(e) => setFormData(prev => ({
                                            ...prev,
                                            projectDetails: { ...prev.projectDetails, projectAddress: e.target.value }
                                        }))}
                                        className="form-control"
                                        rows="2"
                                        placeholder="Surat, Gujarat."
                                        required
                                    />
                                </div>

                                <div className="form-group">
                                    <label className="form-label">Project Area (Sqft) <span className="required">*</span></label>
                                    <input
                                        type="number"
                                        value={formData.projectDetails.projectArea}
                                        onChange={(e) => setFormData(prev => ({
                                            ...prev,
                                            projectDetails: { ...prev.projectDetails, projectArea: parseFloat(e.target.value) || 0 }
                                        }))}
                                        className="form-control"
                                        placeholder="594"
                                        min="0"
                                        step="1"
                                        required
                                    />
                                </div>
                            </div>
                        </div>

                        {/* 1.2 - Client Details */}
                        <div style={{ marginBottom: '30px', paddingTop: '20px', borderTop: '1px solid #e5e7eb' }}>
                            <h4 style={{ fontSize: '16px', fontWeight: '600', marginBottom: '15px', color: '#374151' }}>
                                1.2 - Client Details
                            </h4>
                            <div className="form-row">
                                <div className="form-group">
                                    <label className="form-label">Client Name <span className="required">*</span></label>
                                    <input
                                        type="text"
                                        value={formData.clientDetails.name}
                                        onChange={(e) => setFormData(prev => ({
                                            ...prev,
                                            clientDetails: { ...prev.clientDetails, name: e.target.value }
                                        }))}
                                        className="form-control"
                                        placeholder="Vishal Vasoya"
                                        required
                                    />
                                </div>

                                <div className="form-group">
                                    <label className="form-label">Cell Phone <span className="required">*</span></label>
                                    <input
                                        type="text"
                                        value={formData.clientDetails.cellPhone}
                                        onChange={(e) => setFormData(prev => ({
                                            ...prev,
                                            clientDetails: { ...prev.clientDetails, cellPhone: e.target.value }
                                        }))}
                                        className="form-control"
                                        placeholder="+91-70484 84880"
                                        required
                                    />
                                </div>
                            </div>

                            <div className="form-row">
                                <div className="form-group">
                                    <label className="form-label">Physical Address <span className="required">*</span></label>
                                    <textarea
                                        value={formData.clientDetails.physicalAddress}
                                        onChange={(e) => setFormData(prev => ({
                                            ...prev,
                                            clientDetails: { ...prev.clientDetails, physicalAddress: e.target.value }
                                        }))}
                                        className="form-control"
                                        rows="2"
                                        placeholder="Same as above mentioned"
                                        required
                                    />
                                </div>

                                <div className="form-group">
                                    <label className="form-label">Email ID</label>
                                    <input
                                        type="email"
                                        value={formData.clientDetails.email}
                                        onChange={(e) => setFormData(prev => ({
                                            ...prev,
                                            clientDetails: { ...prev.clientDetails, email: e.target.value }
                                        }))}
                                        className="form-control"
                                        placeholder="client@example.com"
                                    />
                                </div>
                            </div>
                        </div>

                        {/* 1.3 - Designer/Firm Details */}
                        <div style={{ paddingTop: '20px', borderTop: '1px solid #e5e7eb' }}>
                            <h4 style={{ fontSize: '16px', fontWeight: '600', marginBottom: '15px', color: '#374151' }}>
                                1.3 - Designing Firm/Office Details
                            </h4>
                            <div className="form-row">
                                <div className="form-group">
                                    <label className="form-label">Architectural Firm Name <span className="required">*</span></label>
                                    <input
                                        type="text"
                                        value={formData.designerDetails.firmName}
                                        onChange={(e) => setFormData(prev => ({
                                            ...prev,
                                            designerDetails: { ...prev.designerDetails, firmName: e.target.value }
                                        }))}
                                        className="form-control"
                                        placeholder="VIRASTRA DESIGN STUDIO"
                                        required
                                    />
                                </div>

                                <div className="form-group">
                                    <label className="form-label">Head Designers/Architect <span className="required">*</span></label>
                                    <input
                                        type="text"
                                        value={formData.designerDetails.headDesignerName}
                                        onChange={(e) => setFormData(prev => ({
                                            ...prev,
                                            designerDetails: { ...prev.designerDetails, headDesignerName: e.target.value }
                                        }))}
                                        className="form-control"
                                        placeholder="Architect: Priyankkumar Suvagiya"
                                        required
                                    />
                                </div>
                            </div>

                            <div className="form-row">
                                <div className="form-group">
                                    <label className="form-label">Physical Address <span className="required">*</span></label>
                                    <textarea
                                        value={formData.designerDetails.physicalAddress}
                                        onChange={(e) => setFormData(prev => ({
                                            ...prev,
                                            designerDetails: { ...prev.designerDetails, physicalAddress: e.target.value }
                                        }))}
                                        className="form-control"
                                        rows="2"
                                        placeholder="703, Surat Digital Valley, Mota Varachha, Surat, 394105"
                                        required
                                    />
                                </div>

                                <div className="form-group">
                                    <label className="form-label">License Number</label>
                                    <input
                                        type="text"
                                        value={formData.designerDetails.licenseNumber}
                                        onChange={(e) => setFormData(prev => ({
                                            ...prev,
                                            designerDetails: { ...prev.designerDetails, licenseNumber: e.target.value }
                                        }))}
                                        className="form-control"
                                        placeholder="CA/2023/155632"
                                    />
                                </div>
                            </div>

                            <div className="form-row">
                                <div className="form-group">
                                    <label className="form-label">Cell Phone <span className="required">*</span></label>
                                    <input
                                        type="text"
                                        value={formData.designerDetails.cellPhone}
                                        onChange={(e) => setFormData(prev => ({
                                            ...prev,
                                            designerDetails: { ...prev.designerDetails, cellPhone: e.target.value }
                                        }))}
                                        className="form-control"
                                        placeholder="+91-88662 40824"
                                        required
                                    />
                                </div>

                                <div className="form-group">
                                    <label className="form-label">Email ID <span className="required">*</span></label>
                                    <input
                                        type="email"
                                        value={formData.designerDetails.email}
                                        onChange={(e) => setFormData(prev => ({
                                            ...prev,
                                            designerDetails: { ...prev.designerDetails, email: e.target.value }
                                        }))}
                                        className="form-control"
                                        placeholder="virastra.designstudio@gmail.com"
                                        required
                                    />
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                {/* SECTION 2.3 - Inception Fees */}
                <div className="card" style={{ marginBottom: '25px' }}>
                    <div className="card-header">2.3 Inception of Work - Fees</div>
                    <div className="card-body">
                        <div className="form-row">
                            <div className="form-group">
                                <label className="form-label">Total Fees (₹) <span className="required">*</span></label>
                                <input
                                    type="number"
                                    value={formData.inceptionOfWork.totalFees}
                                    onChange={(e) => setFormData(prev => ({
                                        ...prev,
                                        inceptionOfWork: { ...prev.inceptionOfWork, totalFees: parseFloat(e.target.value) || 0 }
                                    }))}
                                    className="form-control"
                                    placeholder="40000"
                                    min="0"
                                    step="0.01"
                                    required
                                />
                            </div>

                            <div className="form-group">
                                <label className="form-label">Per Sqft Rate (₹) <span className="required">*</span></label>
                                <input
                                    type="number"
                                    value={formData.inceptionOfWork.perSqftRate}
                                    onChange={(e) => setFormData(prev => ({
                                        ...prev,
                                        inceptionOfWork: { ...prev.inceptionOfWork, perSqftRate: parseFloat(e.target.value) || 0 }
                                    }))}
                                    className="form-control"
                                    placeholder="70"
                                    min="0"
                                    step="0.01"
                                    required
                                />
                            </div>
                        </div>
                    </div>
                </div>

                {/* SECTION 5 - Charges (4 Work Stage Amounts) */}
                <div className="card" style={{ marginBottom: '25px' }}>
                    <div className="card-header">5. Charges - Payment Stages</div>
                    <div className="card-body">
                        {formData.charges.stages.map((charge, index) => (
                            <div key={index} className="form-group" style={{ marginBottom: '15px' }}>
                                <label className="form-label">
                                    {charge.stageName} - {charge.percentage} <span className="required">*</span>
                                </label>
                                <input
                                    type="number"
                                    value={charge.amount}
                                    onChange={(e) => {
                                        const newStages = [...formData.charges.stages];
                                        newStages[index].amount = parseFloat(e.target.value) || 0;
                                        setFormData(prev => ({
                                            ...prev,
                                            charges: { ...prev.charges, stages: newStages }
                                        }));
                                    }}
                                    className="form-control"
                                    placeholder={index === 0 ? "200000" : index === 3 ? "80000" : "200000"}
                                    min="0"
                                    step="0.01"
                                    required
                                />
                            </div>
                        ))}
                    </div>
                </div>

                {/* SECTION 6 - Close Out Period (Only Months) */}
                <div className="card" style={{ marginBottom: '25px' }}>
                    <div className="card-header">6. Close Out Period - Duration</div>
                    <div className="card-body">
                        <div className="form-group" style={{ maxWidth: '300px' }}>
                            <label className="form-label">Duration (Months) <span className="required">*</span></label>
                            <input
                                type="number"
                                value={formData.closeOutMonths}
                                onChange={(e) => setFormData(prev => ({ ...prev, closeOutMonths: parseFloat(e.target.value) || 0 }))}
                                className="form-control"
                                placeholder="2.5"
                                min="0"
                                step="0.1"
                                required
                            />
                        </div>
                    </div>
                </div>

                {/* Info Box - What's Pre-filled */}
                <div style={{ padding: '20px', background: '#f0f9ff', borderRadius: '8px', marginBottom: '25px', border: '1px solid #0284c7' }}>
                    <h4 style={{ fontSize: '16px', fontWeight: '600', marginBottom: '10px', color: '#0284c7', display: 'flex', alignItems: 'center', gap: '8px' }}>
                        <Info size={18} /> All other sections are pre-filled from template
                    </h4>
                    <p style={{ fontSize: '14px', marginBottom: '8px', color: '#374151' }}>
                        The following sections use default template content and will be included automatically:
                    </p>
                    <ul style={{ fontSize: '14px', color: '#6b7280', marginLeft: '20px' }}>
                        <li>1.1 - Project Details (name, location, address)</li>
                        <li>1.2 - Client Details (name, phone, address)</li>
                        <li>1.3 - Designer/Firm Details (from your settings)</li>
                        <li>2.2 - Work Stages (7 standard stages)</li>
                        <li>3 - General Accord (8 default points)</li>
                        <li>4 - Design Services (8 default points)</li>
                    </ul>
                    <p style={{ fontSize: '13px', marginTop: '12px', color: '#0284c7', fontStyle: 'italic', display: 'flex', alignItems: 'center', gap: '8px' }}>
                        <Lightbulb size={16} /> Tip: After creating, you can view and edit all sections if you need to customize anything!
                    </p>
                </div>

                {/* Action Buttons */}
                <div style={{ display: 'flex', gap: '15px', justifyContent: 'flex-end', padding: '20px 0' }}>
                    <button
                        type="button"
                        onClick={() => navigate('/quotations')}
                        className="btn btn-secondary"
                        style={{ padding: '12px 35px' }}
                    >
                        Cancel
                    </button>

                    <button
                        type="submit"
                        disabled={loading}
                        className="btn btn-primary"
                        style={{
                            padding: '12px 45px',
                            background: loading ? '#9ca3af' : 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
                            border: 'none',
                            display: 'flex',
                            alignItems: 'center',
                            gap: '8px'
                        }}
                    >
                        {loading ? <Loader2 size={18} className="animate-spin" /> : <Plus size={18} />}
                        {loading ? 'Creating...' : 'Create Project Assurance'}
                    </button>
                </div>
            </form>
        </div>
    );
};

export default CreateProfessionalQuotation;
